import type { PaymentsLedgerTranslations } from "../interfaces/paymentsLedger";

export const paymentsLedgerTranslations: PaymentsLedgerTranslations = {
  page_title: "Ledger",
  page_subtitle:
    "Double-entry journal of every payments-runtime operation: reserves, settlements and reversals",
  refresh_button: "Refresh",
  refresh_aria: "Refresh the journal entries of this operation",
  live_updated_at: "Updated {{time}}",
  why_banner:
    "Every entry balances debits against credits per currency — the same invariant the runtime asserts when it posts. An unbalanced entry is a problem with the posting, not with this view.",

  error_load: "Could not load the journal entries",
  error_load_detail:
    "The payments runtime did not answer. No partial amounts are shown: retry to read the full journal.",
  error_retry: "Retry",

  picker_title: "Operations",
  picker_description: "The journal is read per operation: pick one or paste its ID.",
  picker_tab_payouts: "Payouts",
  picker_tab_payins: "Payins",
  picker_tab_swaps: "Conversions",
  picker_search_placeholder: "Paste an operation ID",
  picker_search_hint: "Opens the operation whatever its type.",
  picker_search_invalid: "That ID is not a valid identifier.",
  picker_search_resolving: "Looking up the operation…",
  picker_search_not_found: "That ID has no entries under any operation type.",
  picker_search_failed: "That ID could not be checked. Try again.",
  picker_filter_org_label: "Organization",
  picker_filter_all_orgs: "All organizations",
  picker_filter_status_label: "Status",
  picker_filter_all_statuses: "All statuses",
  picker_filter_corridor_label: "Corridor",
  picker_filter_all_corridors: "All corridors",
  picker_filter_date_label: "Date",
  picker_filter_all_dates: "Any date",
  picker_filter_last_days: "Last {{days}} days",
  picker_clear: "Clear",
  picker_empty_title: "No results",
  picker_empty_description: "No operation matches the selected filters.",
  picker_error: "Could not load the operation list.",
  picker_open_button: "Operations",
  picker_summary: "{{from}}–{{to}} of {{total}}",
  picker_org_unknown: "Unnamed organization",

  status_swap_created: "Created",
  status_swap_completed: "Completed",
  status_swap_failed: "Failed",

  empty_title: "Pick an operation",
  empty_description:
    "Every payments-runtime operation leaves its accounting trail here: which accounts moved, in which direction, and the balance they were left with.",

  header_kind_payout: "Payout",
  header_kind_payin: "Payin",
  header_kind_swap: "Conversion",
  header_operation_id: "Operation ID",
  header_organization: "Organization",
  header_organization_unknown: "Not identified",
  header_created: "First entry",
  header_currencies: "Currencies",
  header_export_csv: "Export CSV",
  header_view_operation: "View operation",
  header_status_unavailable:
    "The operation's current status could not be read. The journal entries are real.",
  copy_success: "Copied",
  copy_aria: "Copy",

  kpi_entries_label: "Entries",
  kpi_entries_meta: "In posting order",
  kpi_legs_label: "Lines",
  kpi_legs_meta: "Across {{accounts}} accounts",
  kpi_balance_label: "Double entry",
  kpi_balance_ok: "Balanced",
  kpi_balance_broken: "Unbalanced",
  kpi_balance_meta: "Debits = credits, per currency",
  kpi_reversals_label: "Reversals",
  kpi_reversals_meta: "Reversing entries that undo a previous one",
  kpi_reversals_none: "None",
  totals_debit: "Debits",
  totals_credit: "Credits",
  unbalanced_heading: "An entry does not balance",
  unbalanced_text:
    "In at least one currency the debits do not equal the credits. The runtime asserts that balance when posting, so this needs a look with the platform team before reconciling.",

  entries_title: "Journal entries",
  entries_description: "Each entry moves at least two accounts and balances on its own.",
  entry_index: "Entry #{{index}}",
  entry_badge_reversal: "Reverses entry #{{index}}",
  entry_badge_reversed: "Reversed by entry #{{index}}",
  entry_balanced: "Balanced",
  entry_unbalanced: "Unbalanced",
  entry_id_label: "Entry ID",
  entry_reason: "Reason",
  no_entries_title: "No entries yet",
  no_entries_description:
    "This operation posted no accounting movement. That happens, for instance, with a payin whose QR was minted and never paid: the entry is written when the money moves.",

  entry_payout_reserve: "Obligation recognized, funds reserved",
  entry_payout_settle: "Payout settled",
  entry_payout_release: "Funds released",
  entry_payin_detected: "Payin received (in suspense)",
  entry_payin_confirmed: "Payin credited",
  entry_swap: "Wallet-to-wallet conversion",
  entry_coloca_top_up: "Coloca top-up to the client's wallet",
  entry_coloca_refund: "Coloca refund to the client's wallet",
  entry_coloca_debit: "Coloca debit from the client's wallet for a manual send",
  entry_coloca_debit_sent: "Manual send of a Coloca debit recorded: it leaves treasury",
  entry_coloca_debit_cancelled: "Coloca debit cancelled: the balance goes back to the client",
  entry_reversal: "Reversal",
  entry_generic_payin: "Payin movement",
  entry_generic_payout: "Payout movement",
  entry_generic_swap: "Conversion movement",
  entry_generic_fee: "Fee movement",
  entry_generic_adjust: "Manual adjustment",
  entry_generic_reverse: "Reversal",

  th_account: "Account",
  th_debit: "Debit",
  th_credit: "Credit",
  th_balance_after: "Resulting balance",
  totals_row_label: "Totals",
  direction_debit: "Debit",
  direction_credit: "Credit",

  account_cash_at_provider: "Cash at provider",
  account_cash_at_provider_hint:
    "Money the corridor's provider holds on Coloca's behalf. An asset: it rises when money comes in and falls when it is paid out.",
  account_client_liability: "Client liability",
  account_client_liability_hint:
    "What Coloca owes the organization. It is the accounting mirror of their wallet balance.",
  account_payout_in_transit: "Payouts in transit",
  account_payout_in_transit_hint:
    "Money already committed to a beneficiary and not settled yet. It clears on paid, failed or cancelled.",
  account_manual_send_in_transit: "Manual sends in transit",
  account_manual_send_in_transit_hint: "What wallet debits hold for a manual send not recorded yet.",
  account_payin_suspense: "Payins in suspense",
  account_payin_suspense_hint:
    "Money received that has not been credited to any organization yet. It must be zero once the payin is confirmed.",
  account_fx_position: "FX position",
  account_fx_position_hint:
    "Bridge between the two currencies of a conversion. Its balance per currency is Coloca's FX exposure, rounding residuals included.",
  account_fee_revenue: "Fee revenue",
  account_fee_revenue_hint:
    "What Coloca charged on the operation: the corridor commission and, when it converted, the conversion fee.",
  account_provider_cost: "Provider cost",
  account_provider_cost_hint:
    "What the provider charged Coloca. Only posted when the corridor's economic model is absorbed.",
  account_tax_payable: "Tax payable · {{tax}}",
  account_tax_payable_hint: "Tax charged or absorbed on the operation that Coloca owes the tax authority.",
  account_tax_expense: "Tax expense · {{tax}}",
  account_tax_expense_hint: "Tax Coloca absorbs on the operation instead of charging it to the client.",
  account_coloca_treasury: "Coloca treasury",
  account_coloca_treasury_hint:
    "Coloca's own funds, already at a provider, credited to clients by staff. Reconciled against the providers' real balances.",
  account_unknown_badge: "Uncatalogued account",
  account_type_asset: "Asset",
  account_type_liability: "Liability",
  account_type_revenue: "Revenue",
  account_type_expense: "Expense",
  account_type_suspense: "Suspense",
  scope_global: "Global",
  scope_provider: "Provider account",
  scope_organization: "Organization",

  rollup_title: "Trial balance per account",
  rollup_description: "Where each account was left after the whole operation, reversals included.",
  th_net: "Net",
  th_last_balance: "Closing balance",
  net_side_debit: "Debit side",
  net_side_credit: "Credit side",
  net_flat: "No net effect",

  trail_title: "Status history",
  trail_description: "When the runtime decided each change, to cross-check against the entries.",
  trail_toggle_show: "Show status history",
  trail_toggle_hide: "Hide status history",
  trail_empty: "This operation recorded no status change.",
  trail_error: "Could not read the status history.",
  trail_initial: "Created",
  trail_source_internal_api: "Internal dashboard",
  trail_source_provider_webhook: "Provider confirmation",
  trail_source_system: "Automatic",
  trail_reason: "Reason",
};
